import React from 'react';
import Container from './Container';

interface ServicePageSectionProps {
  id?: string;
  title?: string;
  subtitle?: string;
  bgColor?: 'white' | 'gray' | 'primary' | 'gradient';
  padding?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
}

/**
 * Standardized section component for service pages — neumorphic theme
 */
const ServicePageSection: React.FC<ServicePageSectionProps> = ({
  id,
  title,
  subtitle,
  bgColor = 'white',
  padding = 'md',
  children,
  className = '',
}) => {
  // All sections use the same neu-bg; 'gray' gets a subtle inset shadow to distinguish
  const getBgClass = () => {
    switch (bgColor) {
      case 'primary':
        return 'bg-gradient-to-br from-neu-accent via-purple-600 to-neu-teal';
      case 'gradient':
        return 'bg-gradient-to-br from-neu-accent via-purple-600 to-neu-teal';
      default:
        return 'bg-neu-bg';
    }
  };

  const getSectionStyle = () => {
    if (bgColor === 'gray') {
      return {
        background: '#E0E5EC',
        boxShadow: 'inset 4px 4px 12px rgb(163,177,198,0.4), inset -4px -4px 12px rgba(255,255,255,0.5)',
      };
    }
    return {};
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'sm': return 'py-8 md:py-12';
      case 'lg': return 'py-16 md:py-20';
      default:   return 'py-12 md:py-16';
    }
  };

  const isColored = bgColor === 'primary' || bgColor === 'gradient';
  const titleColor = isColored ? 'text-white' : 'text-neu-text';
  const subtitleColor = isColored ? 'text-white/80' : 'text-neu-muted';

  return (
    <section
      id={id}
      className={`${getBgClass()} ${getPaddingClass()} ${className}`}
      style={getSectionStyle()}
    >
      <Container>
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && (
              <h2 className={`font-display text-2xl md:text-3xl font-bold mb-4 ${titleColor}`}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={`text-lg max-w-3xl mx-auto ${subtitleColor}`}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </Container>
    </section>
  );
};

export default ServicePageSection;
